import { useEffect, useState } from "react";
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut,
} from "firebase/auth";
import { collection, getDocs, orderBy, query } from "firebase/firestore";
import { auth, firestore } from "../firebase";
import { uploadResume } from "../services/resumeService";

function Admin() {
  const [user, setUser] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [file, setFile] = useState(null);
  const [status, setStatus] = useState("");
  const [busy, setBusy] = useState(false);
  const [messages, setMessages] = useState([]);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [messagesLoading, setMessagesLoading] = useState(false);

  useEffect(() => onAuthStateChanged(auth, setUser), []);

  useEffect(() => {
    if (!user) return undefined;

    let active = true;
    setMessagesLoading(true);

    const loadMessages = async () => {
      try {
        const messagesQuery = query(
          collection(firestore, "Mail", "Message", "entries"),
          orderBy("createdAt", "desc"),
        );
        const snapshot = await getDocs(messagesQuery);
        if (!active) return;

        setMessages(
          snapshot.docs.map((message) => ({
            id: message.id,
            ...message.data(),
          })),
        );
      } catch {
        if (active) setStatus("MESSAGES FAILED TO LOAD");
      } finally {
        if (active) setMessagesLoading(false);
      }
    };

    loadMessages();

    return () => {
      active = false;
    };
  }, [user]);

  const handleLogin = async (event) => {
    event.preventDefault();
    setBusy(true);
    setStatus("");

    try {
      await signInWithEmailAndPassword(auth, email, password);
      setPassword("");
    } catch {
      setStatus("LOGIN FAILED — CHECK YOUR CREDENTIALS");
    } finally {
      setBusy(false);
    }
  };
  const handleUpload = async (event) => {
    event.preventDefault();
    if (!file) return;

    setBusy(true);
    setStatus("");

    try {
      await uploadResume(file);
      setStatus("RESUME UPLOADED — FIRESTORE UPDATED");
      setFile(null);
      event.target.reset();
    } catch {
      setStatus("UPLOAD FAILED — TRY AGAIN LATER");
    } finally {
      setBusy(false);
    }
  };

  if (!user) {
    return (
      <main className="admin-page">
        <h1>ADMIN LOGIN</h1>
        <form onSubmit={handleLogin} className="admin-form">
          <label htmlFor="adminEmail">EMAIL</label>
          <input
            id="adminEmail"
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            required
          />
          <label htmlFor="adminPassword">PASSWORD</label>
          <input
            id="adminPassword"
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            required
          />
          <button className="btn" type="submit" disabled={busy}>
            {busy ? "AUTHENTICATING..." : "LOGIN"}
          </button>
          {status ? <p role="status">{status}</p> : null}
        </form>
      </main>
    );
  }

  return (
    <main className="admin-page">
      <h1>RESUME ADMIN</h1>
      <p className="comment">AUTHENTICATED: {user.email}</p>
      <form onSubmit={handleUpload} className="admin-form">
        <label htmlFor="resumeFile">RESUME FILE</label>
        <input
          id="resumeFile"
          type="file"
          accept="application/pdf"
          onChange={(event) => setFile(event.target.files[0] || null)}
          required
        />
        <button className="btn" type="submit" disabled={busy || !file}>
          {busy ? "UPLOADING..." : "UPLOAD RESUME"}
        </button>
        {status ? <p role="status">{status}</p> : null}
      </form>
      <section className="admin-messages" aria-labelledby="messagesHeading">
        <h2 id="messagesHeading">CONTACT MESSAGES</h2>
        {messagesLoading ? <p>LOADING MESSAGES...</p> : null}
        {!messagesLoading && messages.length === 0 ? (
          <p>NO MESSAGES FOUND.</p>
        ) : null}
        <div className="message-browser">
          <div className="message-list" aria-label="Contact messages">
            {messages.map((message) => (
              <button
                className={`message-list-item${
                  selectedMessage?.id === message.id ? " selected" : ""
                }`}
                key={message.id}
                type="button"
                onClick={() => setSelectedMessage(message)}
              >
                <strong>{message.subject || "NO SUBJECT"}</strong>
                <span>{message.from}</span>
              </button>
            ))}
          </div>
          <article className="message-detail" aria-live="polite">
            {selectedMessage ? (
              <>
                <h3>{selectedMessage.subject || "NO SUBJECT"}</h3>
                <p className="message-meta">FROM: {selectedMessage.from}</p>
                <p className="message-meta">IP: {selectedMessage.ip}</p>
                <p className="message-body">{selectedMessage.text}</p>
              </>
            ) : (
              <p>SELECT A MESSAGE TO VIEW ITS DETAILS.</p>
            )}
          </article>
        </div>
      </section>
      <button
        className="prompt admin-signout"
        type="button"
        onClick={() => signOut(auth)}
      >
        signout
      </button>
    </main>
  );
}

export default Admin;
